import { useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isUserDateInputValid, USER_DATE_YEAR_RANGE_LABEL } from '@crm/shared'
import { useNavigate } from 'react-router-dom'
import {
  createRequest,
  getRequest,
  patchRequest,
  patchRequestStatus,
  type IntakeMeta,
} from '../../../shared/api/requests.api'
import { createItemsBulk, replaceDraftItems } from '../../../shared/api/requestItems.api'
import type { RequestType } from '../../../shared/api/types'
import { toast } from '../../../shared/ui/toastStore'
import type { ItemRow } from '../components/ItemsTable'

// Черновик уже оформлен как заявка (повтор после оборвавшегося ответа, другая
// вкладка). Раньше кнопки молча уводили на заявку, и со стороны это выглядело
// как «нажал — ничего не сохранилось» (жалоба 15.09.2026).
const ALREADY_SUBMITTED_OPEN_TEXT =
  'Черновик уже оформлен как заявка — открываю её. Проверьте позиции'
const ALREADY_SUBMITTED_LIST_TEXT =
  'Черновик уже оформлен как заявка — изменения в него не записаны. Заявка — в списке'

// Набор мутаций для страницы «Новая заявка»: сохранение черновика и создание
// заявки (с промоушеном черновика). Вынесено в отдельный хук, чтобы сама
// NewRequestPage оставалась в пределах 500 строк.
//
// Позиции черновика уходят одним запросом замены (PUT /requests/:id/draft-items):
// сервер удаляет прежние и создаёт новые в одной транзакции. Раньше было два
// запроса — удалить прежние по id и создать новые: сбой второго оставлял
// черновик пустым (жалоба 15.09.2026), а устаревшие id из кэша при гонке
// автосейва давали двойной набор позиций (баг 23.04).
export function useNewRequestSubmit(params: {
  clientId: string
  type: RequestType
  /** Юрлицо продажи (SellerEntity) — уходит и в черновик, и в заявку. */
  sellerEntityId: string
  deadline: string
  notes: string
  /** ВСЕ строки формы — сохраняются в черновик (автосейв/«Сохранить черновик»). */
  allItems: ItemRow[]
  /** Только отмеченные галочкой — уходят в финальную заявку при отправке. */
  selectedItems: ItemRow[]
  draftId: string | null
  intakeMeta: IntakeMeta | null
  /** Только для роли ASSISTANT (Помощник): обязательный менеджер-владелец заявки. */
  ownerManagerId?: string | null
  onError: (err: unknown) => void
}) {
  const {
    clientId,
    type,
    sellerEntityId,
    deadline,
    notes,
    allItems,
    selectedItems,
    draftId,
    intakeMeta,
    ownerManagerId,
    onError,
  } = params
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const submitInProgressRef = useRef(false)
  const autoSavePromiseRef = useRef<Promise<string | null> | null>(null)
  const lastAutoSavedDraftIdRef = useRef<string | null>(null)

  const buildItemsPayload = (rows: ItemRow[]) =>
    rows.map((item) => {
      const pn = item.partNumber.trim().toUpperCase()
      return {
        partNumber: pn || '???',
        // Оригинал заказчика сохраняем как есть (без uppercase) — это то, что
        // клиент реально написал; в КП пойдёт в «Артикул заказчика».
        customerPartNumber: item.customerPartNumber?.trim() || null,
        brand: item.brand.trim() || null,
        quantity: item.quantity,
        unit: item.unit.trim() || null,
        priority: item.priority,
        noteForProcurement: item.note.trim() || null,
        matrix: item.matrix || null,
        category: item.category?.trim() || null,
        comment: notes.trim() || null,
      }
    })

  // Новая заявка без черновика (кнопку нажали раньше первого автосейва).
  const buildAndCreateItems = async (requestId: string, rows: ItemRow[]) => {
    const payload = buildItemsPayload(rows)
    if (payload.length > 0) await createItemsBulk(requestId, payload)
  }

  // Позиции черновика целиком — одна транзакция на сервере.
  const saveDraftItems = (requestId: string, rows: ItemRow[]) =>
    replaceDraftItems(requestId, buildItemsPayload(rows))

  // Дедлайн с кривым годом (62026 из нативного date-input, 1999) в запрос не
  // кладём: сервер ответил бы 422, а автосейв глотает ошибки молча — и вместе с
  // дедлайном переставали сохраняться позиции черновика. Пустое поле → null
  // (очистить), кривое → ключ не отправляем (в БД остаётся прошлый дедлайн).
  // Заодно new Date('275760-12-31').toISOString() больше не бросает RangeError.
  const deadlineInvalid = deadline !== '' && !isUserDateInputValid(deadline)
  const deadlineField =
    deadline === ''
      ? { deadline: null }
      : deadlineInvalid
        ? {}
        : { deadline: new Date(deadline).toISOString() }
  // Явные кнопки («Создать заявку», «Сохранить черновик») показывают причину.
  const assertDeadline = () => {
    if (deadlineInvalid) {
      throw new Error(
        `Год дедлайна вне допустимого диапазона (${USER_DATE_YEAR_RANGE_LABEL}) — проверьте дату`,
      )
    }
  }

  const basePayload = {
    clientId,
    type,
    sellerEntityId,
    ...deadlineField,
    notes: notes.trim() || null,
    intakeMeta,
    // Помощник обязан указать менеджера-владельца; для остальных ролей
    // поле не передаётся вовсе (владелец = менеджер клиента).
    ...(ownerManagerId ? { ownerManagerId } : {}),
  }

  const resolveEditableDraftId = async (): Promise<string | null> => {
    const id = draftId ?? lastAutoSavedDraftIdRef.current
    if (!id) return null

    const fresh = await getRequest(id)
    return fresh.status === 'DRAFT' ? fresh.id : null
  }

  const saveDraftMutation = useMutation({
    mutationFn: async () => {
      assertDeadline()
      if (autoSavePromiseRef.current) {
        await autoSavePromiseRef.current.catch(() => null)
      }

      const candidateDraftId = draftId ?? lastAutoSavedDraftIdRef.current
      const editableDraftId = await resolveEditableDraftId()
      if (editableDraftId) {
        await patchRequest(editableDraftId, basePayload)
        // Черновик хранит ВСЕ строки (allItems), а не только отмеченные — иначе
        // снятие галочки физически удаляло строку из БД (дыра аудита 12.06).
        await saveDraftItems(editableDraftId, allItems)
        return { id: editableDraftId }
      }
      if (candidateDraftId) {
        toast.info(ALREADY_SUBMITTED_LIST_TEXT)
        return { id: candidateDraftId }
      }

      const req = await createRequest({ ...basePayload, asDraft: true })
      // id — до записи позиций: если она не пройдёт, повтор допишет этот же
      // черновик, а не создаст второй.
      lastAutoSavedDraftIdRef.current = req.id
      if (allItems.length > 0) await saveDraftItems(req.id, allItems)
      return req
    },
    onSuccess: (req) => {
      // Удаляем (не инвалидируем) кэш этого черновика — иначе при повторном
      // открытии useQuery вернёт stale-данные из кэша, useEffect заинициализирует
      // форму старыми значениями и выставит draftLoaded=true до прихода refetch.
      // Итог для пользователя: «сохранение запаздывает на одно действие».
      queryClient.removeQueries({ queryKey: ['requests', req.id] })
      void queryClient.invalidateQueries({ queryKey: ['requests'] })
      void navigate('/requests')
    },
    onError,
  })

  const createMutation = useMutation({
    mutationFn: async () => {
      assertDeadline()
      submitInProgressRef.current = true
      try {
        if (autoSavePromiseRef.current) {
          await autoSavePromiseRef.current.catch(() => null)
        }

        const candidateDraftId = draftId ?? lastAutoSavedDraftIdRef.current
        const editableDraftId = await resolveEditableDraftId()
        if (editableDraftId) {
          // Промоушн черновика: обновляем поля, заменяем позиции, меняем статус → NEW.
          // intakeMeta обнуляем — для не-DRAFT оно не нужно и лишь занимает место в БД.
          await patchRequest(editableDraftId, { ...basePayload, intakeMeta: null })
          // Финальная отправка — только отмеченные галочкой строки (selectedItems).
          await saveDraftItems(editableDraftId, selectedItems)
          await patchRequestStatus(editableDraftId, 'NEW')
          return { id: editableDraftId }
        }
        if (candidateDraftId) {
          toast.info(ALREADY_SUBMITTED_OPEN_TEXT)
          return { id: candidateDraftId }
        }

        const req = await createRequest(basePayload)
        await buildAndCreateItems(req.id, selectedItems)
        return req
      } catch (err) {
        submitInProgressRef.current = false
        throw err
      }
    },
    onSuccess: (req) => {
      queryClient.removeQueries({ queryKey: ['requests', req.id] })
      void queryClient.invalidateQueries({ queryKey: ['requests'] })
      void navigate(`/requests/${req.id}`, { replace: true })
    },
    onError,
  })

  // Тихий автосейв: та же логика что в saveDraftMutation, но без navigate
  // и без toast. Возвращает id черновика (созданного или обновлённого),
  // чтобы странница могла сохранить его и продолжить patch-ить тот же
  // черновик при следующих изменениях.
  //
  // Условие "if draftId" — критично: даже если draftRequest ещё не догрузился
  // (например, после invalidate/remove кэша TanStack Query), мы НЕ создаём
  // новый черновик, а заменяем позиции того же. Раньше условие было
  // `if (draftId && draftRequest)` и при гонке кэша автосейв уходил в ветку
  // create → получали дубликаты.
  const autoSave = async (): Promise<string | null> => {
    if (!clientId) return null // без клиента сохранять некуда
    if (submitInProgressRef.current) return null

    const promise = (async () => {
      const editableDraftId = await resolveEditableDraftId()
      if (editableDraftId) {
        await patchRequest(editableDraftId, basePayload)
        // Автосейв черновика хранит ВСЕ строки (allItems) — снятие галочки не
        // должно удалять строку из БД (дыра аудита 12.06).
        await saveDraftItems(editableDraftId, allItems)
        lastAutoSavedDraftIdRef.current = editableDraftId
        return editableDraftId
      }

      if (draftId) return null

      const req = await createRequest({ ...basePayload, asDraft: true })
      // id — до записи позиций: при сбое следующий автосейв допишет этот же
      // черновик, а не создаст второй.
      lastAutoSavedDraftIdRef.current = req.id
      if (allItems.length > 0) await saveDraftItems(req.id, allItems)
      return req.id
    })()

    autoSavePromiseRef.current = promise
    try {
      return await promise
    } finally {
      if (autoSavePromiseRef.current === promise) autoSavePromiseRef.current = null
    }
  }

  return {
    saveDraftMutation,
    createMutation,
    autoSave,
    isPending: saveDraftMutation.isPending || createMutation.isPending,
  }
}
